import React, { useState } from "react";
import axios from "axios";
import { API_BASE } from "../../config/api";
import "./LoginSignup.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import TermsAndConditions from "../../pages/TermsAndConditions";
import { sendSMSOtp } from "../../utils/smsOtp";

const LoginSignup = () => {
  const [formData, setFormData] = useState({ email: "", phone: "" });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [searchParams] = useSearchParams();
  const infoMessage = searchParams.get("message") || "";
  const navigate = useNavigate();
  const [otpMethod, setOtpMethod] = useState("email");

  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [checkboxEnabled, setCheckboxEnabled] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const openTerms = () => {
    setShowTerms(true);
    setCheckboxEnabled(false);
    setAcceptedTerms(false);
  };

  const handleDone = () => {
    setShowTerms(false);
    setCheckboxEnabled(true);
  };

  const normalizePhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");

    if (digits.length === 10) {
      return digits;
    }

    if (digits.length === 12 && digits.startsWith("91")) {
      return digits.slice(2);
    }

    throw new Error("Please enter a valid 10-digit phone number.");
  };

  const clearPendingAuth = () => {
    localStorage.removeItem("pendingEmail");
    localStorage.removeItem("pendingPhone");
    localStorage.removeItem("pendingOtpPurpose");
    localStorage.removeItem("pendingOtpMethod");
    localStorage.removeItem("pendingRegistrationData");
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      setError("You must accept the Terms and Conditions.");
      return;
    }

    // Start loading state
    setIsSubmitting(true);
    setError("");
    setLoadingMessage(otpMethod === "sms" ? "Sending OTP to your phone..." : "Sending OTP to your email...");

    try {
      // Add small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));

      clearPendingAuth();

      if (otpMethod === "sms") {
        const normalizedPhone = normalizePhone(formData.phone);
        await sendSMSOtp(normalizedPhone, "login");

        localStorage.setItem("pendingPhone", normalizedPhone);
        localStorage.setItem("pendingOtpPurpose", "login");
        localStorage.setItem("pendingOtpMethod", "sms");
      } else {
        await axios.post(`${API_BASE}/v1/user/send-otp`, {
          email: formData.email,
          purpose: "login",
        });

        localStorage.setItem("pendingEmail", formData.email.trim().toLowerCase());
        localStorage.setItem("pendingOtpPurpose", "login");
        localStorage.setItem("pendingOtpMethod", "email");
      }
      
      // Add small delay before navigation for smooth transition
      setTimeout(() => {
        navigate("/verify-otp");
      }, 500);
      
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to send OTP");
    } finally {
      // Stop loading state
      setIsSubmitting(false);
      setLoadingMessage("");
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2 className="auth-title">Welcome Back</h2>
        {infoMessage && <div className="auth-info-message">{infoMessage}</div>}
        <div className="otp-method-switch" role="tablist" aria-label="Choose OTP method">
          <button
            type="button"
            className={`otp-method-button ${otpMethod === "email" ? "active" : ""}`}
            onClick={() => {
              setOtpMethod("email");
              setError("");
            }}
          >
            Email OTP
          </button>
          <button
            type="button"
            className={`otp-method-button ${otpMethod === "sms" ? "active" : ""}`}
            onClick={() => {
              setOtpMethod("sms");
              setError("");
            }}
          >
            SMS OTP
          </button>
        </div>
        <form onSubmit={handleLogin}>
          {otpMethod === "email" ? (
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
            />
          ) : (
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number (10 digits)"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          )}
          <div className="terms-row">
            <input
              type="checkbox"
              id="terms-login"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              required
              disabled={!checkboxEnabled}
            />
            <label htmlFor="terms-login">
              I accept the{" "}
              <span
                className="terms-link"
                onClick={(e) => {
                  e.preventDefault();
                  openTerms();
                }}
              >
                Terms and Conditions
              </span>
            </label>
          </div>
          {error && <p className="auth-error">{error}</p>}
          <button 
            type="submit" 
            className={`auth-button ${isSubmitting ? 'auth-button-loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="button-content">
                <span className="spinner"></span>
                {otpMethod === "sms" ? "Sending SMS OTP..." : "Sending OTP..."}
              </span>
            ) : (
              otpMethod === "sms" ? "Send SMS OTP" : "Send OTP"
            )}
          </button>
        </form>
        <div className="auth-footer">
          <p onClick={() => navigate("/forgot-password")}>Forgot Password?</p>
          <p>
            New user? <span onClick={() => navigate("/register")}>Register</span>
          </p>
        </div>
      </div>
      
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-message">{loadingMessage}</p>
          </div>
        </div>
      )}
      
      {showTerms && (
        <div className="terms-modal-overlay">
          <div className="terms-modal-shell">
            <button
              type="button"
              className="terms-modal-close"
              aria-label="Close terms and conditions"
              onClick={() => setShowTerms(false)}
            >
              &times;
            </button>
            <TermsAndConditions onDone={handleDone} />
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginSignup;
